import { useCallback, useEffect, useMemo, useState } from 'react';
import { useAuthContext } from '@/auth/useAuthContext';

const STORAGE_KEY_PREFIX = 'cakto-seen-once';

/**
 * Chave de armazenamento do marcador, por usuário.
 *
 * O `featureKey` carrega versão (ex.: `products-v2-welcome:v1`) para que uma
 * reformulação futura do conteúdo possa voltar a aparecer sem precisar limpar
 * o storage de ninguém: basta subir a versão na chave.
 */
export function buildSeenOnceKey(featureKey, userId) {
  return `${STORAGE_KEY_PREFIX}:${featureKey}:${userId}`;
}

export function readSeen(storageKey) {
  try {
    return localStorage.getItem(storageKey) === '1';
  } catch {
    // Sem storage (aba anônima travada, ITP, storage cheio) tratamos como "já viu".
    // Melhor não mostrar do que mostrar em todo carregamento, para sempre.
    return true;
  }
}

export function writeSeen(storageKey) {
  try {
    localStorage.setItem(storageKey, '1');
  } catch {
    // ignore storage errors
  }
}

/**
 * Marcador de "isso só aparece uma vez", escopado por usuário.
 *
 * Mesmo padrão de persistência já usado em `useMyProductsListView`: chave
 * prefixada + id do usuário, com try/catch em toda leitura e escrita.
 *
 * Limite conhecido: o marcador vive no navegador. Trocar de máquina ou limpar
 * os dados do site faz o conteúdo aparecer de novo. Levar o dado para um campo
 * de perfil no backend não se resolve só trocando `readSeen` por uma chamada da
 * API: o `Announcements` chama `readSeen` direto, no render, e conta com a
 * leitura síncrona. Uma Promise ali é truthy, e todo aviso passaria por visto.
 *
 * @param {string} featureKey identificador versionado do conteúdo
 * @returns {{ seen: boolean, ready: boolean, markSeen: () => void }}
 */
export default function useSeenOnce(featureKey) {
  const { user } = useAuthContext();
  const userId = user?.id;

  const storageKey = useMemo(
    () => (userId ? buildSeenOnceKey(featureKey, userId) : null),
    [featureKey, userId]
  );

  // Começa como "já viu" de propósito: enquanto o usuário não carregou não dá
  // para saber, e o custo de um falso negativo (não mostrar) é menor do que o
  // de um falso positivo (piscar o modal e marcar como visto sem o usuário ler).
  const [seen, setSeen] = useState(true);

  useEffect(() => {
    if (!storageKey) return;

    setSeen(readSeen(storageKey));
  }, [storageKey]);

  const markSeen = useCallback(() => {
    setSeen(true);

    if (!storageKey) return;

    writeSeen(storageKey);
  }, [storageKey]);

  return {
    seen,
    ready: Boolean(storageKey),
    markSeen,
  };
}
